import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, 
  Loader2, 
  UserPlus,
  Phone,
  User,
  AtSign,
  CheckCircle,
  Search,
  Users,
  Gift,
  Shield,
  Store,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";

export default function DrawShopMode() {
  const { id } = useParams();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState("");
  const [existingMember, setExistingMember] = useState(null);
  const [searching, setSearching] = useState(false);
  const [rgpdConsent, setRgpdConsent] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    custom_field: ""
  });

  useEffect(() => {
    loadDraw();
  }, [id]);

  const loadDraw = async () => {
    try {
      const res = await api.get(`/public/draw/${id}`);
      setDraw(res.data);
    } catch (error) {
      toast.error("Tirage non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const searchByPhone = async () => {
    if (phoneSearch.length < 8) return;
    
    setSearching(true);
    try {
      const res = await api.get(`/members/search?phone=${phoneSearch}`);
      if (res.data) {
        setExistingMember(res.data);
        setFormData({
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.phone || phoneSearch,
          custom_field: ""
        });
        toast.success("Client reconnu !");
      } else {
        setExistingMember(null);
        setFormData(prev => ({ ...prev, phone: phoneSearch }));
      }
    } catch (error) {
      setExistingMember(null);
      setFormData(prev => ({ ...prev, phone: phoneSearch }));
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rgpdConsent) {
      toast.error("Le consentement RGPD est obligatoire");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/public/draw/${id}/register`, {
        ...formData,
        rgpd_consent: true,
        source: "shop_mode"
      });
      setSuccess(true);
      toast.success("Participant inscrit !");
      
      // Reset form after delay
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: "", email: "", phone: "", custom_field: "" });
        setPhoneSearch("");
        setExistingMember(null);
        setRgpdConsent(false);
      }, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!draw) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Tirage non trouvé</p>
            <Link to="/">
              <Button className="mt-4">Retour à l'accueil</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-lg border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Store className="w-6 h-6 text-amber-500" />
            <div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                Mode Boutique
              </h1>
              <p className="text-xs text-slate-400">{draw.organizer_name}</p>
            </div>
          </div>
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
            {draw.participants?.length || 0} participants
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col items-center justify-center py-20"
                >
                  <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Inscription réussie !</h2>
                  <p className="text-slate-400">Le participant a été ajouté au tirage</p>
                  <p className="text-amber-400 text-sm mt-4">Nouveau participant dans 3 secondes...</p>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-amber-500" />
                        Inscrire un participant
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        {draw.title}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Phone Search */}
                      <div className="mb-6 p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                        <Label className="text-slate-300 text-sm mb-2 block">
                          Recherche par téléphone
                        </Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Phone className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                            <Input
                              type="tel"
                              placeholder="06 12 34 56 78"
                              value={phoneSearch}
                              onChange={(e) => setPhoneSearch(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && searchByPhone()}
                              className="pl-10 bg-slate-800 border-slate-600 text-white"
                            />
                          </div>
                          <Button 
                            onClick={searchByPhone}
                            disabled={searching || phoneSearch.length < 8}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            {searching ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Search className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        {existingMember && (
                          <div className="mt-3 p-2 rounded bg-green-500/20 border border-green-500/30 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-green-300">
                              Client reconnu : {existingMember.name}
                            </span>
                          </div>
                        )}
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-slate-300">Nom complet *</Label>
                            <div className="relative">
                              <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                              <Input
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="pl-10 bg-slate-800 border-slate-600 text-white"
                                placeholder="Jean Dupont"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-300">Email *</Label>
                            <div className="relative">
                              <AtSign className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                              <Input
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="pl-10 bg-slate-800 border-slate-600 text-white"
                                placeholder="email@exemple.com"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-slate-300">Téléphone *</Label>
                          <div className="relative">
                            <Phone className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                            <Input
                              type="tel"
                              required
                              value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              className="pl-10 bg-slate-800 border-slate-600 text-white"
                              placeholder="06 12 34 56 78"
                            />
                          </div>
                        </div>

                        {draw.custom_field_label && (
                          <div className="space-y-2">
                            <Label className="text-slate-300">
                              {draw.custom_field_label} {draw.custom_field_required && "*"}
                            </Label>
                            <Input
                              value={formData.custom_field}
                              onChange={(e) => setFormData({ ...formData, custom_field: e.target.value })}
                              required={draw.custom_field_required}
                              className="bg-slate-800 border-slate-600 text-white"
                            />
                          </div>
                        )}

                        {/* RGPD Consent */}
                        <div className="p-4 rounded-lg bg-slate-700/30 border border-slate-600">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                              checked={rgpdConsent}
                              onCheckedChange={setRgpdConsent}
                              className="mt-0.5 border-slate-500"
                            />
                            <span className="text-sm text-slate-300 leading-relaxed">
                              Le participant accepte que ses données soient traitées dans le cadre de ce tirage au sort et consent à recevoir les communications relatives aux résultats. 
                              <span className="text-slate-400"> (Conformité RGPD)</span>
                            </span>
                          </label>
                        </div>

                        <Button 
                          type="submit" 
                          disabled={submitting || !rgpdConsent}
                          className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Inscription...
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Inscrire le participant
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Side Info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Draw Info */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">
                  {draw.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {draw.organizer_logo && (
                  <img 
                    src={draw.organizer_logo} 
                    alt={draw.organizer_name}
                    className="h-12 object-contain"
                  />
                )}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Users className="w-4 h-4 text-blue-400" />
                    {draw.participants?.length || 0} participants
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Gift className="w-4 h-4 text-amber-400" />
                    {draw.prizes?.length || 0} lots
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Prizes */}
            {draw.prizes?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-500" />
                    Lots à gagner
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {draw.prizes.map((prize, index) => (
                    <div key={prize.id || index} className="flex items-center gap-3 text-sm">
                      <div className="w-6 h-6 rounded bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-xs">
                        {index + 1}
                      </div>
                      <span className="text-slate-300">{prize.name}</span>
                      <Badge variant="outline" className="ml-auto text-xs border-slate-600 text-slate-400">
                        x{prize.quantity}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* RGPD Info */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-white mb-1">Conformité RGPD</h4>
                    <p className="text-xs text-slate-400">
                      Les participants peuvent demander la suppression de leurs données à tout moment.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
